import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Users, BookOpen, BarChart3, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { SiteNav, SiteFooter } from './HomePage';

const DemoRequestPage = () => {
  const [form, setForm] = useState({ name: '', email: '', organization: '', role: '', program_type: '', youth_count: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization) { toast.error('Please fill in all required fields'); return; }
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Demo request sent!');
      } else {
        toast.error('Failed to send. Please email info@yasconnect.co directly.');
      }
    } catch (err) {
      toast.error('Connection error. Please email info@yasconnect.co directly.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteNav />

      <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #2D2824 100%)' }} className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3">Request a Demo</h1>
          <p className="text-white/60 max-w-lg mx-auto">See how YAS Connect can transform your youth program. We will walk you through the platform and answer all your questions.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Form */}
          <div>
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-bold text-gray-700 block mb-1">Your Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="First Last" className="rounded-xl" /></div>
                  <div><label className="text-xs font-bold text-gray-700 block mb-1">Email *</label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@org.com" className="rounded-xl" /></div>
                </div>
                <div><label className="text-xs font-bold text-gray-700 block mb-1">Organization *</label><Input value={form.organization} onChange={e => setForm({...form, organization: e.target.value})} placeholder="Your organization name" className="rounded-xl" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-bold text-gray-700 block mb-1">Your Role</label><Input value={form.role} onChange={e => setForm({...form, role: e.target.value})} placeholder="Program Director, etc." className="rounded-xl" /></div>
                  <div><label className="text-xs font-bold text-gray-700 block mb-1">Approx. Youth Count</label><Input value={form.youth_count} onChange={e => setForm({...form, youth_count: e.target.value})} placeholder="e.g., 100-500" className="rounded-xl" /></div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Program Type</label>
                  <select value={form.program_type} onChange={e => setForm({...form, program_type: e.target.value})} className="w-full p-2.5 rounded-xl border border-gray-200 text-sm">
                    <option value="">Select...</option>
                    <option value="syep">Summer Youth Employment (SYEP)</option>
                    <option value="school">School District Program</option>
                    <option value="nonprofit">Nonprofit Youth Program</option>
                    <option value="workforce">Workforce Development</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div><label className="text-xs font-bold text-gray-700 block mb-1">Tell us about your program</label><Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="What are your biggest challenges? What are you hoping to solve?" rows={3} className="rounded-xl" /></div>
                <Button type="submit" className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold py-5 text-base">
                  <Send className="w-4 h-4 mr-2" /> Send Request
                </Button>
              </form>
            ) : (
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Received!</h3>
                <p className="text-gray-600">We will contact you within 24 hours to schedule your demo. Check your email for confirmation.</p>
              </motion.div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">What you will see in the demo:</h3>
              <div className="space-y-3">
                {[
                  { icon: Users, text: 'How to onboard 500+ youth in minutes with CSV import' },
                  { icon: BookOpen, text: 'The 13 interactive workshops youth actually complete' },
                  { icon: BarChart3, text: 'Real-time staff dashboard with AI-powered youth reports' },
                  { icon: CheckCircle, text: 'How the platform replaces paper workbooks and spreadsheets' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                    <item.icon className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-orange-50 border border-orange-100">
              <h4 className="font-bold text-sm text-gray-900 mb-3">Contact us directly:</h4>
              <div className="space-y-2">
                <p className="text-sm text-gray-600 flex items-center gap-2"><Mail className="w-4 h-4 text-orange-500" /> info@yasconnect.co</p>
                <p className="text-sm text-gray-600 flex items-center gap-2"><MapPin className="w-4 h-4 text-orange-500" /> Newark, NJ</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default DemoRequestPage;
